import * as React from 'react';
import { motion } from 'framer-motion';
import { MenuItem } from './MenuItem';
import { Link, Image } from '@/components';
import { JEWELRY3D_URL, PagePath } from '@/layouts/shared';
import { Category } from '@/features/products/list/const';
import { useCategories } from '@/contexts/profile';

const variants = {
  open: {
    transition: { staggerChildren: 0.07, delayChildren: 0.2 },
  },
  closed: {
    transition: { staggerChildren: 0.05, staggerDirection: -1 },
  },
};
type NavigationProps = {
  isAuth: boolean;
  handleClick: () => void;
  categoryHeader?: {
    code: string;
    image: string;
  };
};
export const Navigation = ({ isAuth, handleClick, categoryHeader }: NavigationProps) => {
  const categories: Category[] | null = useCategories();
  const listRef = React.useRef<HTMLUListElement>(null);
  const [maxHeight, setMaxHeight] = React.useState<number | undefined>(undefined);

  // Body bị khoá cuộn khi mở menu → menu phải tự cuộn được trong phần màn hình còn lại
  React.useLayoutEffect(() => {
    const update = () => {
      const el = listRef.current;
      if (!el) return;
      const vh = window.visualViewport?.height ?? window.innerHeight;
      const top = Math.max(el.getBoundingClientRect().top, 0);
      setMaxHeight(Math.max(vh - top, 200));
    };
    update();
    window.addEventListener('resize', update);
    window.visualViewport?.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('resize', update);
    };
  }, []);

  return (
    <motion.ul
      ref={listRef}
      variants={variants}
      style={{
        height: maxHeight ?? '100vh',
        // chừa chỗ cho thanh menu dưới (fixed) để mục cuối không bị che
        paddingBottom: 'calc(6rem + env(safe-area-inset-bottom))',
        WebkitOverflowScrolling: 'touch',
      }}
      className="tw-pt-6 tw-absolute tw-top-[45px] tw-left-0 tw-bg-dark tw-w-[300px] tw-bg-white tw-overflow-y-auto tw-overscroll-contain"
    >
      <div className="tw-grid tw-gap-y-3">
        <div className="tw-grid tw-gap-y-[10px] tw-px-6">
          <Link
            url={{ pathname: PagePath.SupportPage }}
            className="tw-font-medium hover:!tw-underline tw-uppercase tw-text-xs"
          >
            <div className="tw-text-secondary-70" onClick={handleClick}>
              Hỗ trợ
            </div>
          </Link>
          <Link
            url="/tin-tuc/"
            className="tw-text-xs tw-font-medium hover:!tw-underline tw-uppercase"
          >
            <div className="tw-text-secondary-70" onClick={handleClick}>
              Tin tức
            </div>
          </Link>
          {/* tạm thời: thiết kế riêng chuyển qua jewelry3d.ai */}
          <a
            href={JEWELRY3D_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="tw-text-xs tw-font-medium hover:!tw-underline tw-uppercase tw-no-underline"
          >
            <div className="tw-text-secondary-70" onClick={handleClick}>
              AI Thiết kế
            </div>
          </a>
        </div>
        <div className="tw-bg-primary-main tw-text-white tw-text-sm tw-py-2 tw-text-center">
          Danh mục sản phẩm
        </div>
        <div className="tw-flex tw-flex-col tw-gap-y-[10px] tw-px-6">
          {categories && (
            <div className="tw-flex tw-flex-col tw-gap-y-[10px]">
              <div className="tw-text-xs tw-uppercase tw-font-bold">Dòng hàng</div>
              {(categories as Category[])
                .filter((c) => c.is_collection)
                .map((c, index) => (
                  <MenuItem
                    item={c}
                    key={`category-${c.code}-${index}`}
                    handleClick={handleClick}
                  />
                ))}
              <div className="tw-text-xs tw-uppercase tw-font-bold">Chủng loại</div>
              {(categories as Category[])
                .filter((c) => !c.is_collection)
                .map((c, index) => (
                  <MenuItem
                    item={c}
                    key={`category-${c.code}-${index}`}
                    handleClick={handleClick}
                  />
                ))}
            </div>
          )}

          {categoryHeader && (
            <div className="tw-w-[200px] tw-pb-[70%] tw-relative tw-h-0" onClick={handleClick}>
              <Link
                url={{
                  pathname: PagePath.CategoryPage,
                  query: { id: categoryHeader?.code },
                }}
              >
                <Image
                  alt={categoryHeader?.code}
                  src={categoryHeader?.image}
                  className="tw-w-full tw-h-full !tw-absolute tw-top-0 tw-left-0"
                  objectFit="cover"
                />
              </Link>
            </div>
          )}
        </div>
      </div>
    </motion.ul>
  );
};
// const itemIds = [0, 1, 2, 3, 4];
